import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import SidebarLeft from "../../components/SidebarLeft";
import SidebarRight from "../../components/SidebarRight";
import QuestionCard from "../../components/QuestionCard";
import { ThemeContext } from "../../components/ThemeProvider";
import FloatingEditModal from "../../components/FloatingEditModal";
import DemoQuestionsFeed from "../../components/DemoQuestionsFeedV2";
import api from "../../api";
import Swal from "sweetalert2";
import LockCard from "../../components/LockCard";
import Loader from "../../components/Loader";
import { useAuth } from "../../context/AuthContext";
import { useSearch } from "../../context/SearchContext";

const QUESTIONS_TAB_ID = "__questions__";

function Home() {
  const { theme } = useContext(ThemeContext);
  const [allPosts, setAllPosts] = useState([]);
  const [questionGroups, setQuestionGroups] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ads, setAds] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [singlePostId, setSinglePostId] = useState(null);
  const [selectedHashtags, setSelectedHashtags] = useState([]);
  const [activeSort, setActiveSort] = useState("new");
  const hashtagTabsRef = useRef(null);
  const dragStateRef = useRef({
    isDragging: false,
    startX: 0,
    startScrollLeft: 0,
  });
  const { user: loggedInUser, hasActiveSubscription } = useAuth();
  const { searchTerm, updateSearch } = useSearch();
  const userId = loggedInUser?.id;

  useEffect(() => {
    let metaTag = document.querySelector('meta[name="robots"]');

    if (!metaTag) {
      metaTag = document.createElement("meta");
      metaTag.setAttribute("name", "robots");
      document.head.appendChild(metaTag);
    }

    metaTag.setAttribute("content", "noindex, nofollow");

    return () => {
      metaTag.setAttribute("content", "noindex, nofollow");
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const idParam = params.get("id");
    if (idParam) {
      setSinglePostId(parseInt(idParam, 10));
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [postsRes, questionsRes, adsRes] = await Promise.all([
          api.get("/topics", { params: { user_id: userId } }),
          api.get("/questions", { params: { user_id: userId } }),
          api.get("/add-banners"),
        ]);

        const posts = (postsRes.data?.posts || [])
          .filter((post) => post.status === "approved")
          .filter((post) => {
            if (!loggedInUser) return true;
            return post.user_id !== loggedInUser.id;
          })
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

        setAllPosts(posts);
        setQuestionGroups(questionsRes.data?.questions || []);

        const advisements = adsRes.data?.data?.Advisement || [];
        const centerAds = advisements.filter(
          (ad) =>
            (ad.position === "Center Left" || ad.position === "Center Right") &&
            ad.show === "1",
        );

        setAds(
          [...centerAds].sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at),
          ),
        );
      } catch (err) {
        Swal.fire("Error!", "Failed to load data", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, loggedInUser]);

  const stripHtml = (html = "") => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const handleHashtagSelect = (hashtag) => {
    setSelectedHashtags((prev) => {
      if (prev.includes(hashtag)) {
        return prev;
      }

      return [...prev, hashtag];
    });
  };

  const handleHashtagRemove = (hashtag) => {
    setSelectedHashtags((prev) => prev.filter((tag) => tag !== hashtag));
  };

  const handleHashtagWheel = (event) => {
    const container = hashtagTabsRef.current;
    if (!container) {
      return;
    }

    const hasHorizontalOverflow = container.scrollWidth > container.clientWidth;
    if (!hasHorizontalOverflow) {
      return;
    }

    if (event.deltaY !== 0 || event.deltaX !== 0) {
      event.preventDefault();
      container.scrollLeft += event.deltaY || event.deltaX;
    }
  };

  const handleHashtagMouseDown = (event) => {
    const container = hashtagTabsRef.current;
    if (!container || container.scrollWidth <= container.clientWidth) {
      return;
    }

    dragStateRef.current = {
      isDragging: true,
      startX: event.clientX,
      startScrollLeft: container.scrollLeft,
    };
  };

  const handleHashtagMouseMove = (event) => {
    const container = hashtagTabsRef.current;
    const dragState = dragStateRef.current;

    if (!container || !dragState.isDragging) {
      return;
    }

    event.preventDefault();
    const deltaX = event.clientX - dragState.startX;
    container.scrollLeft = dragState.startScrollLeft - deltaX;
  };

  const stopHashtagDragging = () => {
    if (dragStateRef.current.isDragging) {
      dragStateRef.current.isDragging = false;
    }
  };

  const shuffleArray = (arr) => {
    const shuffled = [...arr];
    for (let i = shuffled.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  useEffect(() => {
    if (singlePostId || selectedCategory === QUESTIONS_TAB_ID) {
      return;
    }

    let filtered = allPosts;

    if (selectedHashtags.length > 0) {
      const hashtagMatches = allPosts.filter((post) => {
        const postTags = (post.slug || "")
          .split(",")
          .map((tag) => tag.trim().toLowerCase().replace("#", ""));

        return selectedHashtags.some((hashtag) =>
          postTags.includes(hashtag.toLowerCase()),
        );
      });

      const hashtagMismatches = allPosts.filter((post) => {
        const postTags = (post.slug || "")
          .split(",")
          .map((tag) => tag.trim().toLowerCase().replace("#", ""));

        return !selectedHashtags.some((hashtag) =>
          postTags.includes(hashtag.toLowerCase()),
        );
      });

      filtered = [...hashtagMatches, ...hashtagMismatches];
    } else if (selectedCategory === null) {
      const topRecent = [...allPosts].slice(0, 5);
      const topLiked = [...allPosts]
        .sort((a, b) => (b.likes_count || 0) - (a.likes_count || 0))
        .slice(0, 5);

      const topPostIds = new Set([
        ...topRecent.map((post) => post.id),
        ...topLiked.map((post) => post.id),
      ]);

      const remainingPosts = allPosts.filter((post) => !topPostIds.has(post.id));
      const combined = [...topRecent, ...topLiked, ...remainingPosts];
      const uniqueIds = new Set();

      filtered = combined.filter((post) => {
        if (uniqueIds.has(post.id)) return false;
        uniqueIds.add(post.id);
        return true;
      });

      filtered = shuffleArray(filtered);
    } else {
      filtered = filtered.filter((post) =>
        post.topics?.some((topic) => topic.id === selectedCategory),
      );
    }

    if (searchTerm.trim()) {
      filtered = filtered.filter((post) => {
        const textContent = stripHtml(post.description || "");
        const title = post.title || "";
        const tags = (post.topics || []).map((topic) => topic.name || topic.slug || "").join(" ");
        let searchQuery = searchTerm.toLowerCase();

        if (!searchQuery.startsWith("#")) {
          searchQuery = searchQuery.replace(/^#+/, "");
        } else {
          searchQuery = searchQuery.substring(1);
        }

        const combined = `${title} ${textContent} ${tags}`.toLowerCase();
        return combined.includes(searchQuery);
      });
    }

    if (activeSort === "top") {
      filtered = [...filtered].sort((a, b) => (b.likes_count || 0) - (a.likes_count || 0));
    } else if (activeSort === "controversial") {
      filtered = [...filtered].sort(
        (a, b) => ((b.dislikes_count || 0) + (b.likes_count || 0)) - ((a.dislikes_count || 0) + (a.likes_count || 0)),
      );
    }

    setQuestions(filtered);
  }, [activeSort, allPosts, searchTerm, selectedCategory, selectedHashtags, singlePostId]);

  const filteredQuestionGroups = useMemo(() => {
    let filtered = [...questionGroups];

    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase().replace(/^#+/, "");

      filtered = filtered.filter((group) => {
        const searchableText = [
          group.question,
          group.hashtags,
          group.topic?.name,
          ...(group.posts || []).map((post) => `${post.title || ""} ${post.description || ""}`),
        ]
          .join(" ")
          .toLowerCase();

        return searchableText.includes(query);
      });
    }

    return filtered.sort((a, b) => {
      if (activeSort === "top") {
        const likesA = (a.posts || []).reduce((sum, post) => sum + (post.likes_count || 0), 0);
        const likesB = (b.posts || []).reduce((sum, post) => sum + (post.likes_count || 0), 0);
        return likesB - likesA;
      }

      return new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0);
    });
  }, [activeSort, questionGroups, searchTerm]);

  return (
    <div className={`main-layout ${theme}-theme`}>
      <div className="container">
        <div className="content-wrapper" style={{ display: "flex" }}>
          <SidebarLeft
            onCategorySelect={setSelectedCategory}
            selectedCategory={selectedCategory}
            onSearch={updateSearch}
            onFilterChange={setActiveSort}
          />

          <main className="main-section-parent">
            <div className="top-bar-wrapper">
              <SidebarLeft
                onCategorySelect={setSelectedCategory}
                selectedCategory={selectedCategory}
                onSearch={updateSearch}
                onFilterChange={setActiveSort}
              />
            </div>

            {loading && <Loader />}

            {!loading && selectedCategory === QUESTIONS_TAB_ID && (
              <DemoQuestionsFeed items={filteredQuestionGroups} />
            )}

            {!loading && selectedCategory !== QUESTIONS_TAB_ID && selectedHashtags.length > 0 && (
              <div
                ref={hashtagTabsRef}
                className="hashtag-filter-tabs"
                onWheel={handleHashtagWheel}
                onMouseDown={handleHashtagMouseDown}
                onMouseMove={handleHashtagMouseMove}
                onMouseLeave={stopHashtagDragging}
                onMouseUp={stopHashtagDragging}
              >
                <div className="hashtag-filter-track">
                  {selectedHashtags.map((hashtag) => (
                    <div key={hashtag} className="hashtag-tab">
                      <span>#{hashtag}</span>
                      <button onClick={() => handleHashtagRemove(hashtag)}>x</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!loading && selectedCategory !== QUESTIONS_TAB_ID && (
              (!singlePostId) ? (
                <>
                  {questions.length === 0 && (
                    <div className="no-post-box custom-no-post">
                      <h5>No posts in this topic yet</h5>
                      <p>Be the first to share something here!</p>
                    </div>
                  )}

                  {(() => {
                    const elements = [];
                    let adIndex = 0;

                    questions.forEach((post, index) => {
                      if (post.lock === 1 && !hasActiveSubscription) {
                        elements.push(
                          <LockCard
                            key={`lock-${post.id}`}
                            post={post}
                            onHashtagSelect={handleHashtagSelect}
                          />,
                        );
                      } else {
                        elements.push(
                          <QuestionCard
                            key={post.id}
                            question={post}
                            onHashtagSelect={handleHashtagSelect}
                          />,
                        );
                      }

                      if (!hasActiveSubscription && (index + 1) % 3 === 0 && ads.length > 0) {
                        const ad = ads[adIndex % ads.length];
                        if (ad?.image_video) {
                          elements.push(
                            <div className="single-components-ad" key={`ad-${index}-${ad.id}`}>
                              <a href={ad.url} target="_blank" rel="noopener noreferrer">
                                <img
                                  src={ad.image_video}
                                  alt="Advertisement"
                                  className="ad-image"
                                  style={{ width: "100%", height: "auto" }}
                                  onError={(e) => {
                                    const slot = e.currentTarget.closest(".single-components-ad");
                                    if (slot) {
                                      slot.style.display = "none";
                                    }
                                  }}
                                />
                              </a>
                            </div>,
                          );
                          adIndex += 1;
                        }
                      }
                    });

                    return elements;
                  })()}
                </>
              ) : (
                (() => {
                  const post = allPosts.find((item) => item.id === singlePostId);

                  if (!post) {
                    return (
                      <div className="no-post-box">
                        <p>Post Unavailable.</p>
                      </div>
                    );
                  }

                  return (
                    <div className="single-post-view">
                      <QuestionCard
                        key={post.id}
                        question={post}
                        onHashtagSelect={handleHashtagSelect}
                      />

                      <div className="text-center mt-4">
                        <button
                          className="view-more-btn"
                          onClick={() => {
                            window.history.replaceState({}, "", "/");
                            setSinglePostId(null);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}
                          style={{
                            background: "transparent",
                            border: "none",
                            fontSize: "1rem",
                            color: "var(--category-text, #34495e)",
                            cursor: "pointer",
                            textDecoration: "none",
                            padding: "0px",
                            backgroundColor: "rgba(255, 255, 255, 0)",
                            borderRadius: "0px",
                            fontWeight: 600,
                            marginTop: "10px",
                            borderBottom: "2px solid yellow",
                          }}
                        >
                          View More Posts
                        </button>
                      </div>
                    </div>
                  );
                })()
              )
            )}
          </main>

          <SidebarRight />
          <FloatingEditModal
            defaultCategory={selectedCategory}
            hideTrigger={selectedCategory === QUESTIONS_TAB_ID}
          />
        </div>
      </div>
    </div>
  );
}

export default Home;
